import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useSearch } from './hooks/useSearch';
import LoginForm from './components/common/LoginForm';
import ServiceVenturesLogo from './components/common/ServiceVenturesLogo';
import SearchBar from './components/common/SearchBar';
import Navigation from './components/common/Navigation';
import Dashboard from './components/sections/Dashboard';
import Financial from './components/sections/Financial';
import Strategy from './components/sections/Strategy';
import Portfolio from './components/sections/Portfolio';
import Pipeline from './components/sections/Pipeline';
import Decisions from './components/sections/Decisions';
import Supporting from './components/sections/Supporting';
import Analysis from './components/sections/Analysis';
import AnalysisDetail from './components/sections/AnalysisDetail';
import PortfolioCompany from './components/portfolio/PortfolioCompany';
import { companiesData } from './data/centralizedData';
import { colors } from './utils/constants';
import InvestmentTracking from './components/sections/InvestmentTracking';
import ScribeUpAnalysis from './components/analysis/detailed/ScribeUpAnalysis';
import Conferlending from './components/analysis/detailed/Conferlending';
import Silvuranalysis from './components/analysis/detailed/Silvuranalysis';

// Component to handle analysis routing
const AnalysisRouter = ({ onSectionChange }) => {
  const { analysisId } = useParams();
  
  switch (analysisId) {
    case 'scribe-up-analysis':
      return <ScribeUpAnalysis onSectionChange={onSectionChange} />;
    case 'direct-deposit-analysis':
      return <Silvuranalysis onSectionChange={onSectionChange} />;
    case 'confer-lending-analysis':
      return <Conferlending onSectionChange={onSectionChange} />;
    default:
      return <AnalysisDetail analysisId={analysisId} onSectionChange={onSectionChange} />;
  }
};

// Component to handle portfolio company routing
const PortfolioRouter = ({ onSectionChange }) => {
  const { companyId } = useParams();
  const companyData = companiesData[companyId];

  if (!companyData) {
    return <Navigate to="/portfolio" replace />;
  }

  return <PortfolioCompany companyData={companyData} onSectionChange={onSectionChange} />;
};

// Main app content component that uses router
const AppContent = () => {
  const { isAuthenticated, password, setPassword, loginError, handleLogin, handleLogout } = useAuth();
  const { searchTerm, searchResults, isSearching, handleSearch, handleSearchResultClick } = useSearch();
  const navigate = useNavigate();

  // Navigation handler that uses React Router
  const handleSectionChange = (sectionId) => {
    navigate(`/${sectionId}`);
  };

  // Get current section from URL
  const getCurrentSection = () => {
    const path = window.location.pathname;
    if (path === '/' || path === '/dashboard') return 'dashboard';
    return path.substring(1); // Remove leading slash
  };

  const activeSection = getCurrentSection();

  // Scroll to top when route changes
  useEffect(() => {
    const mainContent = document.getElementById('main-content-panel');
    if (mainContent) {
      mainContent.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [activeSection]);

  if (!isAuthenticated) {
    return <LoginForm password={password} setPassword={setPassword} loginError={loginError} handleLogin={handleLogin} />;
  }

  return (
    <div style={{backgroundColor: colors.blue}} className="h-screen flex">
      {/* Left Navigation Panel */}
      <div style={{backgroundColor: colors.blue}} className="w-80 flex-shrink-0 overflow-y-auto hide-scrollbar flex items-start justify-center py-6">
        <div className="w-72">
          <Navigation activeSection={activeSection} onSectionChange={handleSectionChange} />
        </div>
      </div>

      {/* Right Content Panel */}
      <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-6">
            <ServiceVenturesLogo size="small" />
            <h1 className="text-xl font-semibold text-gray-900">Board Portal</h1>
          </div>
          <div className="flex items-center space-x-4">
            <SearchBar 
              searchTerm={searchTerm}
              searchResults={searchResults}
              isSearching={isSearching}
              handleSearch={handleSearch}
              handleSearchResultClick={handleSearchResultClick}
              onSectionChange={handleSectionChange}
            />
            <button 
              onClick={handleLogout}
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded border border-gray-300 hover:border-gray-400 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div id="main-content-panel" className="flex-1 overflow-y-auto">
          <div className="p-8">
            <div className="max-w-5xl mx-auto">
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard onSectionChange={handleSectionChange} />} />
                <Route path="/financial" element={<Financial />} />
                <Route path="/strategy" element={<Strategy />} />
                <Route path="/portfolio" element={<Portfolio onSectionChange={handleSectionChange} />} />
                <Route path="/pipeline" element={<Pipeline />} />
                <Route path="/analysis" element={<Analysis onSectionChange={handleSectionChange} />} />
                <Route path="/analysis/:analysisId" element={<AnalysisRouter onSectionChange={handleSectionChange} />} />
                <Route path="/decisions" element={<Decisions />} />
                <Route path="/supporting" element={<Supporting />} />
                <Route path="/investmentTracking" element={<InvestmentTracking />} />
                
                {/* Portfolio company routes */}
                <Route path="/portfolio/:companyId" element={<PortfolioRouter onSectionChange={handleSectionChange} />} />
                
                {/* Catch-all route for unmatched URLs */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App component with Router wrapper
const ServiceVenturesBoardPortal = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default ServiceVenturesBoardPortal;